import { useCallback, useEffect, useMemo } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import api from '../../api'
import Responsive from '../../assets/config/Responsive'
import DownloadSectionIcon from '../../assets/icons/JSX/DownloadSectionIcon'
import KiteAboutUsIcon from '../../assets/icons/JSX/KiteAboutUsIcon'
import Colors from '../../assets/styles/Colors'
import { Container } from '../../assets/styles/GlobalStyle'
import Loader from '../../components/Loader'
import WelcomeBannerBgTabletNew from '../../assets/img/welcome-banner.svg'
import { useAppSelector } from '../../store'
import { AppState } from '../../store/appSlice'
import { MainPageText } from '../../domain/types'
import AboutUsSwiper from '../../components/AboutUsSwiper'
import AutoplayCarousel from '../../components/AutoplayCarousel'
import PlayStores from '../../components/PlayStores'
import PromoCodeSlider from '../../components/v2/sliders/PromoCodeSlider'
import ProductCategorySlider from './ProductCategorySlider'

// Constants
const PAGE_SLUGS = {
  FIRST_BLOCK: 'main-page-first-block',
  SECOND_BLOCK: 'main-page-second-block',
  THIRD_BLOCK: 'main-page-third-block',
  FOURTH_BLOCK: 'main-page-fourth-block',
} as const

const PRODUCTION_HOSTNAMES = ['frani.com', 'www.frani.com'] as const

const CATEGORY_ROUTE = '/category'

// Helper function to get page text by slug
const getPageTextBySlug = (
  textList: MainPageText[] | undefined,
  slug: string,
  lang: string,
): MainPageText['translations'][keyof MainPageText['translations']] | null => {
  if (!textList) return null
  const pageText = textList.find((item) => item.slug === slug)
  if (!pageText?.translations) return null
  return pageText.translations[lang as keyof typeof pageText.translations] || null
}

// Sub-components
interface WelcomeBannerProps {
  title?: string
  excerpt?: string
  buttonText?: string
  onButtonClick: () => void
}

const WelcomeBanner = ({ title, excerpt, buttonText, onButtonClick }: WelcomeBannerProps) => (
  <WelcomeSection>
    <WelcomeBlock>
      <h1>
        {title}
        <span />
      </h1>
      <p>{excerpt}</p>
      <button type="button" onClick={onButtonClick} aria-label={buttonText || 'Price calculation'}>
        {buttonText}
      </button>
    </WelcomeBlock>
  </WelcomeSection>
)

interface SectionHeaderProps {
  title?: string
  excerpt?: string
}

const SectionHeader = ({ title, excerpt }: SectionHeaderProps) => (
  <ServiceHead>
    <h2>{title}</h2>
    <p>{excerpt}</p>
  </ServiceHead>
)

interface ServiceSectionWrapperProps {
  children: React.ReactNode
  icon?: React.ReactNode
}

const ServiceSectionWrapper = ({ children, icon }: ServiceSectionWrapperProps) => (
  <ServiceSection>
    {icon}
    <Container>{children}</Container>
  </ServiceSection>
)

// Main Component
const Landing = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { lang } = useAppSelector((root: { app: AppState }) => root.app)

  const { data: mainpageTextList, isLoading: isMainPageTextLoading } = api.useGetMainPageTextListQuery(undefined)

  // Memoize page texts to avoid repeated find() operations
  const pageTexts = useMemo(() => {
    if (!mainpageTextList || !lang) return null

    return {
      firstBlock: getPageTextBySlug(mainpageTextList, PAGE_SLUGS.FIRST_BLOCK, lang),
      secondBlock: getPageTextBySlug(mainpageTextList, PAGE_SLUGS.SECOND_BLOCK, lang),
      thirdBlock: getPageTextBySlug(mainpageTextList, PAGE_SLUGS.THIRD_BLOCK, lang),
      fourthBlock: getPageTextBySlug(mainpageTextList, PAGE_SLUGS.FOURTH_BLOCK, lang),
    }
  }, [mainpageTextList, lang])

  // Check if production hostname
  const isProductionHostname = useMemo(
    () => PRODUCTION_HOSTNAMES.includes(window.location.hostname as (typeof PRODUCTION_HOSTNAMES)[number]),
    [],
  )

  // Cleanup effect
  useEffect(() => {
    localStorage.removeItem('new_user')
  }, [])

  // Navigation handlers
  const handleCategoryNavigation = useCallback(() => {
    navigate(CATEGORY_ROUTE)
  }, [navigate])

  if (isMainPageTextLoading) {
    return <Loader />
  }

  return (
    <>
      <Helmet>
        <title>{t('landingSeoTitle')}</title>
        <meta name="description" content={t('landingSeoDescription')} />
        <link rel="canonical" href={window.location.href} />
      </Helmet>

      {!isProductionHostname && <PromoCodeSlider />}

      <WelcomeBanner
        title={pageTexts?.firstBlock?.title}
        excerpt={pageTexts?.firstBlock?.excerpt}
        buttonText={pageTexts?.firstBlock?.body}
        onButtonClick={handleCategoryNavigation}
      />

      <ServiceSectionWrapper>
        <SectionHeader title={pageTexts?.secondBlock?.title} excerpt={pageTexts?.secondBlock?.excerpt} />
        <ProductCategorySlider />
      </ServiceSectionWrapper>

      <AutoplayCarousel />

      <ServiceSectionWrapper icon={<KiteAboutUsIconStyle />}>
        <SectionHeader title={pageTexts?.thirdBlock?.title} excerpt={pageTexts?.thirdBlock?.excerpt} />
        <AboutUsSwiper />
      </ServiceSectionWrapper>

      <DownloadSection>
        <DownloadSectionIconStyle />
        <Container>
          <SectionHeader title={pageTexts?.fourthBlock?.title} excerpt={pageTexts?.fourthBlock?.excerpt} />
          <PlayStores />
        </Container>
      </DownloadSection>
    </>
  )
}

// Styled Components
const WelcomeSection = styled.section`
  background-image: url(${WelcomeBannerBgTabletNew});
  background-repeat: no-repeat;
  background-position-x: center;
  background-size: 100vw auto;
  background-position-y: 40%;
  height: 800px;
  margin-bottom: 20px;

  ${Responsive.laptop} {
    height: 900px;
  }

  ${Responsive.tablet} {
    background-position-y: 80%;
    height: 850px;
  }

  ${Responsive.mobile} {
    height: calc(100vh - 65px);
    background-position: 27% 92%;
    margin-bottom: 0;
    background-size: 300%;
  }
`

const WelcomeBlock = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  padding-top: 80px;
  height: 55%;

  span {
    position: relative;

    svg {
      display: none;
      position: absolute;
      left: 0;
      bottom: -12px;
      width: 99%;
    }
  }

  h1 {
    font-size: 50px;
    color: ${Colors.Black};
    line-height: 65px;
    text-align: center;
  }

  p {
    width: 600px;
    line-height: 30px;
    font-size: 20px;
    color: ${Colors.SilverChalice};
    text-align: center;
    margin-inline: 20px;
  }

  button {
    width: 260px;
    height: 64px;
    min-height: 64px;
    border-radius: 4px;
    background-color: ${Colors.primaryRed};
    outline: none;
    border: 0;
    color: ${Colors.white};
    font-size: 18px;
    margin-top: 30px;
    cursor: pointer;
    transition: all 200ms ease-in;

    &:hover {
      transform: scale(1.12);
      background-color: transparent;
      color: ${Colors.primaryBlack};
      border: 1px solid ${Colors.primaryBlack};
    }
  }

  ${Responsive.mobile} {
    padding-top: 50px;
    height: unset;

    h1 {
      font-size: 26px;
      line-height: 35px;
    }

    p {
      margin-top: 10px;
      font-size: 14px;
      line-height: 20px;
      width: 80%;
    }

    button {
      width: 220px;
      height: 48px;
      min-height: 48px;
      font-size: 14px;
      margin-top: 10px;
    }
  }
`

const ServiceSection = styled.section`
  background-color: ${Colors.Alabaster};
  padding-top: 85px;
  padding-bottom: 85px;
  position: relative;
  overflow: hidden;

  ${Responsive.mobile} {
    padding-top: 0;
    padding-bottom: 40px;
  }
`

const ServiceHead = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  margin-bottom: 65px;
  position: sticky;
  z-index: 1;

  h2 {
    font-size: 20px;
    color: ${Colors.Black};
    margin-bottom: 35px;
    text-align: center;
  }

  p {
    font-size: 18px;
    color: ${Colors.SilverChalice};
    margin-bottom: 35px;
    width: 100%;
    text-align: center;
  }

  ${Responsive.mobile} {
    margin-bottom: 30px;

    h2 {
      font-size: 18px;
      margin-bottom: 16px;
    }

    p {
      font-size: 12px;
      margin-bottom: 0;
      width: 100%;
    }
  }
`

const KiteAboutUsIconStyle = styled(KiteAboutUsIcon)`
  position: absolute;
  top: 17px;
  right: 66px;

  ${Responsive.tablet} {
    display: none;
  }

  ${Responsive.mobile} {
    display: none;
  }
`

const DownloadSectionIconStyle = styled(DownloadSectionIcon)`
  position: absolute;
  top: 17px;
  right: 66px;

  ${Responsive.tablet} {
    display: none;
  }

  ${Responsive.mobile} {
    display: none;
  }
`

const DownloadSection = styled.section`
  background-color: ${Colors.white};
  padding-top: 85px;
  padding-bottom: 85px;
  position: relative;
  overflow-y: hidden;

  ${Responsive.mobile} {
    padding-top: 30px;
    padding-bottom: 40px;
  }
`

export default Landing
